import React, {useEffect} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import classnames from 'classnames';
import getOptions from '@pluto/get-options';
import Tooltip from '@material-ui/core/Tooltip';
import ErrorIcon from '@material-ui/icons/Error';
import {Select, MenuItem, TextField} from '@material-ui/core';
import {
    getYearValues,
    msToTime,
    COPY_RIGHT_START_YEAR,
    COPY_RIGHT_END_YEAR,
    getFrom,
} from '@pluto/utils';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {useForm, useField} from 'react-final-form-hooks';
import {getFilmLabels} from '@pluto/get-film-labels';
import GenresContainer from '@pluto/genres/GenresConnected';
import SubGenresContainer from '@pluto/subGenres/SubGenresConnected';
import {Label, Role} from 'QCTools/QCToolsMetadata/QCToolsMetadataTypes';
import {getSubGenres} from '@pluto/subGenres/SubGenresContainer';
import SourceRegionContainer from '@pluto/source-region/SourceRegionConnected';
import {
    MetadataGeneral,
    MetadataContentType,
    contentTypeRequiredFieldsConfig,
    VideoTypeData,
} from './ContentItemViewMetadataTypes';
import {getPartItems} from './ContentItemViewMetadataContainer';
import {Genre, SubGenre} from '../../types';
import {
    getTextWithEmptyFieldError,
    handleIsRequiredLabel,
    getEmptyFieldValidation,
    getTextValue,
    getGenreFieldValidation,
    getSubGenreFieldValidation,
    getAllotmentFieldValidation,
    getHeaderEditBlock,
    openEditMode,
} from './ContentItemViewMetadataUtils';
import {getUUID} from 'ContentItem/Search/SearchHandlers';
//  eslint-disable-next-line @typescript-eslint/no-var-requires
const TimeInput = require('time-input');

export interface RoleEditMode {
    id: number;
    role: string;
    name: string;
}

export type FieldState = {
    value: any;
    name: string;
};

export type FormType = {
    getFieldState: (val: string) => FieldState;
};

export interface InitialValues {
    actors: RoleEditMode[];
    directors: RoleEditMode[];
    producers: RoleEditMode[];
    composers: RoleEditMode[];
    writers: RoleEditMode[];
    releaseYear: number;
    copyrightYear: number[];
    copyrightHolder: string;
}

export interface QCMetadataGeneralProps {
    genres: Genre[];
    isEditMode: boolean;
    setIsEditMode: (value: boolean) => void;
    general: MetadataGeneral;
    actorsInitialValues: RoleEditMode[];
    directorsInitialValues: RoleEditMode[];
    producersInitialValues: RoleEditMode[];
    composersInitialValues: RoleEditMode[];
    writersInitialValues: RoleEditMode[];
    taskId: number;
    allotment: number | null;
    setContentItemViewMetadataGeneral: (id: number, param: any) => void;
    metadataContentType: MetadataContentType;
    videoData: {
        video: VideoTypeData;
    };
    jobDeliveryDate: string;
}

export type RoleMetadataType = {
    id: number;
    label: string;
    roleMetaType: string;
};

export type ContentItemGenreType = {
    id?: number;
    label: string;
};

export interface QcMetadataPayloadBody {
    copyrightYear: number;
    copyrightHolder: string;
    releaseDate: string;
    roleMeta: RoleMetadataType[];
    contentItemGenre: ContentItemGenreType;
}

const onSubmit = () => {};

export const onRoleSelectChange = curry(
    (roleValues: RoleEditMode[], index: number, onFieldChange: any, e: any) => {
        const newRolesValue = Array.from(roleValues);
        const newValue = e.target.value;

        newRolesValue[index].role = newValue;
        onFieldChange(newRolesValue);
    }
);

export const getRoleItems = (
    isEditMode: boolean,
    roleItemsEditMode: JSX.Element[],
    roleItems: JSX.Element[]
) => {
    return isEditMode ? roleItemsEditMode : roleItems;
};

export const onRoleNameChange = curry(
    (roleValues: RoleEditMode[], index: number, onFieldChange: any, e: any) => {
        const newNamesValue = Array.from(roleValues);
        const newValue = e.target.value;

        newNamesValue[index].name = newValue;
        onFieldChange(newNamesValue);
    }
);

export const mapToEditModeLayout = curry(
    (currentField: any, item: RoleEditMode, index: number): JSX.Element => {
        const fieldValue = currentField.input.value;
        const onFieldChange = currentField.input.onChange;
        const onSelectChange = onRoleSelectChange(fieldValue, index, onFieldChange);
        const onNameChange = onRoleNameChange(fieldValue, index, onFieldChange);

        return (
            <div className="part-wrapper" key={index}>
                <Select
                    className="qc-metadata__role-select part-label_editable"
                    onChange={onSelectChange}
                    value={fieldValue[index]?.role || item.role}
                >
                    <MenuItem value="actor">Actor</MenuItem>
                    <MenuItem value="director">Director</MenuItem>
                    <MenuItem value="composer">Composer</MenuItem>
                    <MenuItem value="producer">Producer</MenuItem>
                    <MenuItem value="writer">Writer</MenuItem>
                </Select>
                <TextField
                    className="qc-metadata__name-input part-value_editable"
                    onChange={onNameChange}
                    value={fieldValue[index]?.name || item.name}
                />
            </div>
        );
    }
);

export const getEditModePartItems = (
    field: any,
    roleValues: RoleEditMode[]
): JSX.Element[] => {
    const mapRoleValues = mapToEditModeLayout(field);

    return roleValues.map(mapRoleValues);
};

export const getReleaseYearDatePicker = (
    isEditMode: boolean,
    fieldValue: string,
    fieldChange: (value: any) => void,
    defaultModeValue: string | undefined | JSX.Element,
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string,
    fieldLabel: string
) => {
    if (isEditMode) {
        return (
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                    inputVariant="filled"
                    disableFuture
                    variant="inline"
                    KeyboardButtonProps={{
                        size: 'small',
                    }}
                    format="MM/dd/yyyy"
                    size="small"
                    margin="normal"
                    placeholder="MM/dd/yyyy"
                    value={fieldValue || null}
                    onChange={fieldChange}
                />
            </MuiPickersUtilsProvider>
        );
    }

    return getEmptyFieldError(defaultModeValue, fieldLabel);
};

export const getYearSelect = (
    isEditMode: boolean,
    fieldValue: string,
    fieldChange: (value: any) => void,
    yearsOptions: JSX.Element[],
    defaultModeValue: number | undefined
) => {
    if (isEditMode) {
        return (
            <Select
                className="qc-metadata__editable-item"
                value={fieldValue}
                onChange={fieldChange}
            >
                {yearsOptions}
            </Select>
        );
    }

    return defaultModeValue;
};

export const getGenresSelected = (
    isEditMode: boolean,
    possibleGenreLabels: string[],
    genreFieldValue: string,
    genreFieldChange: (value: React.ChangeEvent<any>) => void,
    defaultValue: any,
    defaultGenre: string[],
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string,
    fieldLabel: string
) => {
    if (isEditMode) {
        return (
            <GenresContainer
                genreFieldValue={genreFieldValue}
                genreFieldChange={genreFieldChange}
            />
        );
    }

    if (!defaultGenre.length) {
        return getEmptyFieldError(defaultValue, fieldLabel);
    }

    if (
        defaultGenre &&
        !defaultGenre.every((el: string) => possibleGenreLabels?.indexOf(el) !== -1)
    ) {
        return (
            <>
                <div className="metadata-item__value_wrong">{defaultValue}</div>
                <div className="metadata-item__value-error invalid-option">
                    <div className="metadata-item__value-error_icon">
                        <ErrorIcon />
                    </div>
                    <div>Invalid option, select new</div>
                </div>
            </>
        );
    }

    return defaultValue;
};

export const getSubGenresSelected = (
    isEditMode: boolean,
    genreFieldValue: string,
    subGenreFieldValue: string,
    subGenreFieldChange: (value: React.ChangeEvent<any>) => void,
    defaultValue: any,
    defaultSubGenre: string[],
    possibleSubGenreLabels: any,
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string,
    fieldLabel: string
) => {
    if (isEditMode) {
        return (
            <SubGenresContainer
                subGenreFieldValue={subGenreFieldValue}
                subGenreFieldChange={subGenreFieldChange}
                genreFieldValue={genreFieldValue}
            />
        );
    }

    if (!defaultSubGenre.length) {
        return getEmptyFieldError(defaultValue, fieldLabel);
    }

    if (
        defaultSubGenre &&
        !defaultSubGenre.every((el: string) => possibleSubGenreLabels?.indexOf(el) !== -1)
    ) {
        return (
            <>
                <div className="metadata-item__value_wrong">{defaultValue}</div>
                <div className="metadata-item__value-error invalid-option">
                    <div className="metadata-item__value-error_icon">
                        <ErrorIcon />
                    </div>
                    <div>Invalid option, select new</div>
                </div>
            </>
        );
    }

    return defaultSubGenre;
};

export const cancelEditedChanges = wrap((setIsEditMode: (value: boolean) => void) => {
    setIsEditMode(false);
});

export const generatePayload = (
    formParams: any,
    releaseDate: string,
    roleMeta: RoleMetadataType[],
    defaultGenre: Label[],
    defaultSubGenre: Label[]
) => {
    const {
        copyrightYear,
        copyrightHolder,
        actors,
        directors,
        producers,
        composers,
        writers,
        defaultGenreLabel,
        defaultSubGenreLabel,
        sourceRegionLabel,
        allotment,
    } = formParams;

    const contentItemGenre = getContentItemGenre(defaultGenreLabel, defaultGenre[0]);
    const contentItemSubGenre = getContentItemGenre(
        defaultSubGenreLabel,
        defaultSubGenre[0]
    );

    const roleMetadata = getRolesMeta(actors, directors, producers, composers, writers);
    let generatedPayload: any = {
        copyrightYear,
        copyrightHolder,
        allotment,
        sourceRegion: sourceRegionLabel,
        releaseDate,
        roleMeta: roleMetadata,
    };

    if (contentItemGenre.label) {
        generatedPayload = {
            ...generatedPayload,
            contentItemGenre,
        };
    }

    if (contentItemSubGenre.label) {
        generatedPayload = {
            ...generatedPayload,
            contentItemSubGenre,
        };
    }

    return generatedPayload;
};

export const saveEditedChanges = wrap(
    (
        setIsEditMode: (value: boolean) => void,
        setContentItemViewMetadataGeneral: any,
        id: number,
        form: any,
        roleMeta: RoleMetadataType[],
        defaultGenre: Label[],
        defaultSubGenre: Label[]
    ) => {
        setIsEditMode(false);
        const {
            copyrightYear,
            releaseDate,
            copyrightHolder,
            actors,
            directors,
            producers,
            composers,
            writers,
            defaultGenreLabel,
            defaultSubGenreLabel,
            sourceRegionLabel,
            allotment,
        } = form.getState().values;
        const param = generatePayload(
            {
                copyrightYear,
                copyrightHolder,
                actors,
                directors,
                producers,
                composers,
                writers,
                defaultGenreLabel,
                defaultSubGenreLabel,
                sourceRegionLabel,
                allotment,
            },
            releaseDate,
            roleMeta,
            defaultGenre,
            defaultSubGenre
        );

        setContentItemViewMetadataGeneral({
            id,
            param,
        });
    }
);

export const getRolesMetaObject = (
    id: number,
    label: string,
    roleMetaType: string,
    existingId: number[]
) => {
    if (existingId.includes(id)) {
        return null;
    }

    existingId.push(id);
    return {
        id,
        label,
        roleMetaType,
    };
};

export const mapRolesMeta = ({id, role, name}: RoleEditMode) => ({
    id,
    roleMetaType: role,
    label: name,
});

export const getRolesMeta = (
    roleActors: RoleEditMode[],
    roleDirectors: RoleEditMode[],
    roleProducers: RoleEditMode[],
    roleComposers: RoleEditMode[],
    roleWriters: RoleEditMode[]
) =>
    [
        ...roleActors,
        ...roleDirectors,
        ...roleProducers,
        ...roleComposers,
        ...roleWriters,
    ].map(mapRolesMeta);

export const getContentItemGenre = (
    value: string,
    defaultValue: ContentItemGenreType
) => {
    if (!defaultValue) {
        return {
            label: value,
        };
    }

    const {id, label} = defaultValue;
    const isSameValue = value === label;

    if (isSameValue) {
        return {
            id,
            label,
        };
    }

    return {
        label: value,
    };
};

export const getDefaultReleaseDate = (
    releaseDate: string,
    fieldLabel: string,
    metadataContentTypeRequiredFields: string[],
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string
) => {
    if (!releaseDate) {
        if (metadataContentTypeRequiredFields.includes(fieldLabel)) {
            return getEmptyFieldError(releaseDate, fieldLabel);
        }

        return '';
    }

    return moment(releaseDate).format('MM/DD/yyyy');
};

export const getSourceRegion = (
    isEditMode: boolean,
    fieldChange: (val: string) => void,
    fieldValue: string,
    sourceRegion: string | undefined,
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string,
    metadataContentTypeRequiredFields: string[],
    fieldLabel: string
) => {
    if (isEditMode) {
        return (
            <SourceRegionContainer
                onChange={fieldChange}
                value={fieldValue?.toLowerCase()}
            />
        );
    }

    if (!sourceRegion && metadataContentTypeRequiredFields.includes(fieldLabel)) {
        return getEmptyFieldError(sourceRegion, fieldLabel);
    }

    return sourceRegion?.toUpperCase();
};

export const onGenreChange = curry(
    (onGenreFieldChange: any, onSubGengeFieldChange: any, value: any) => {
        onGenreFieldChange(value);
        onSubGengeFieldChange('');
    }
);

export const handleAllotmentChange = curry(
    (onChange: (val: number) => void, val: string) => {
        const timeArr = val.split(':');
        const milliseconds = parseInt(timeArr.pop() as string);
        const validTime = `${timeArr.join(':')}Z`;
        const dateMilliseconds = new Date(`1970-01-01T${validTime}`).getTime();

        onChange(dateMilliseconds + milliseconds);
    }
);

export const getAllotment = (
    onChange: (val: number) => void,
    value: number,
    isEditMode: boolean,
    allotment: number | null,
    videoData: VideoTypeData,
    getEmptyFieldError: (text: any, fieldLabel: string) => JSX.Element | string
) => {
    if (isEditMode) {
        const time = isNaN(value) ? value : msToTime(value);
        return <TimeInput value={time} onChange={handleAllotmentChange(onChange)} />;
    }

    if (!allotment && typeof allotment !== 'number') {
        // cause allotment can be 0
        return getEmptyFieldError(allotment, 'allotment');
    }

    return msToTime(allotment);
};

function ContentItemViewMetadataGeneral(props: QCMetadataGeneralProps) {
    const {
        genres,
        isEditMode,
        setIsEditMode,
        general,
        allotment,
        actorsInitialValues,
        directorsInitialValues,
        producersInitialValues,
        composersInitialValues,
        writersInitialValues,
        taskId,
        setContentItemViewMetadataGeneral,
        metadataContentType,
        videoData,
        jobDeliveryDate,
    } = props;

    const {
        title,
        roles,
        partner,
        partnerHash,
        availsId,
        contentType,
        sourceRegion,
        copyrightHolder,
        copyrightYear,
        defaultGenre,
        defaultSubGenre,
        releaseDate,
        episodeNumber,
        seasonNumber,
        mamId,
    } = general;
    const defaultGenreLabel = defaultGenre[0]?.label || '';
    const defaultSubGenreLabel = defaultSubGenre[0]?.label || '';
    const defaultGenreLabels = defaultGenre.map(getFrom('label')) as string[];
    const defaultSubGenreLabels = defaultSubGenre.map(getFrom('label')) as string[];
    const possibleGenreLabels = genres.map(getFrom<Genre>('label')) as string[];
    const possibleSubGenreLabels = getSubGenres(genres, defaultGenreLabel)?.map(
        getFrom<SubGenre>('label')
    ) as string[];
    const {actors, directors, producers, composers, writers, roleMeta} = roles;

    let initialValues = {};

    useEffect(() => {
        initialValues = {
            actors: actorsInitialValues,
            directors: directorsInitialValues,
            producers: producersInitialValues,
            composers: composersInitialValues,
            writers: writersInitialValues,
            releaseDate,
            copyrightYear,
            copyrightHolder,
            defaultGenreLabel,
            defaultSubGenreLabel,
            allotment,
            sourceRegionLabel: sourceRegion,
        };
        form.reset(initialValues);
    }, [isEditMode]);

    const {form, valid} = useForm({
        onSubmit,
        initialValues,
    });
    const yearsOptions = getYearValues(COPY_RIGHT_START_YEAR, COPY_RIGHT_END_YEAR);
    const selectYearOptions = getOptions(yearsOptions);

    const metadataContentTypeRequiredFields =
        contentTypeRequiredFieldsConfig[metadataContentType];
    const getEmptyFieldError = getTextWithEmptyFieldError(
        metadataContentTypeRequiredFields
    );
    const getIsRequiredLabel = handleIsRequiredLabel(metadataContentTypeRequiredFields);

    const defaultReleaseDate = getDefaultReleaseDate(
        releaseDate,
        'release-date',
        metadataContentTypeRequiredFields,
        getEmptyFieldError
    );

    const releaseDateField = useField(
        'releaseDate',
        form,
        getEmptyFieldValidation(metadataContentTypeRequiredFields, 'release-date') as any
    );
    const copyrightHolderField = useField('copyrightHolder', form);
    const copyrightYearField = useField('copyrightYear', form);

    const actorsField = useField('actors', form);
    const directorsField = useField('directors', form);
    const producersField = useField('producers', form);
    const composersField = useField('composers', form);
    const writersField = useField('writers', form);
    const sourceRegionField = useField(
        'sourceRegionLabel',
        form,
        getEmptyFieldValidation(metadataContentTypeRequiredFields, 'source-region') as any
    );
    const genreField = useField(
        'defaultGenreLabel',
        form,
        getGenreFieldValidation(
            metadataContentTypeRequiredFields,
            'genres',
            genres
        ) as any
    );
    const subGenreField = useField(
        'defaultSubGenreLabel',
        form,
        getSubGenreFieldValidation(
            metadataContentTypeRequiredFields,
            'sub-genres',
            genres,
            genreField.input.value
        ) as any
    );

    const allotmentField = useField('allotment', form, getAllotmentFieldValidation);

    const defaultGenreItem = getFilmLabels(defaultGenre, 'film-label');
    const defaultSubGenreItem = getFilmLabels(defaultSubGenre, 'film-label');
    const directorItemsEditMode = getEditModePartItems(
        directorsField,
        directorsInitialValues
    );
    const producerItemsEditMode = getEditModePartItems(
        producersField,
        producersInitialValues
    );
    const composersItemsEditMode = getEditModePartItems(
        composersField,
        composersInitialValues
    );
    const writersItemsEditMode = getEditModePartItems(writersField, writersInitialValues);
    const actorItemsEditMode = getEditModePartItems(actorsField, actorsInitialValues);

    const actorItems = getPartItems(
        'Actor',
        actors.map(getFrom<Role>('label')) as string[]
    );
    const directorItems = getPartItems(
        'Director',
        directors.map(getFrom<Role>('label')) as string[]
    );
    const producerItems = getPartItems(
        'Producer',
        producers.map(getFrom<Role>('label')) as string[]
    );
    const composerItems = getPartItems(
        'Composer',
        composers.map(getFrom<Role>('label')) as string[]
    );
    const writerItems = getPartItems(
        'Writer',
        writers.map(getFrom<Role>('label')) as string[]
    );

    const defaultVideoData = {
        duration: 0,
        dropFrame: true,
        frameRate: {
            numerator: 0,
            denominator: 0,
        },
    };

    return (
        <>
            <div className="qc-metadata__general-header">
                <div className="qc-metadata__general-header-text">General metadata</div>
                {!jobDeliveryDate &&
                    getHeaderEditBlock(
                        isEditMode,
                        valid,
                        openEditMode(setIsEditMode),
                        saveEditedChanges(
                            setIsEditMode,
                            setContentItemViewMetadataGeneral,
                            taskId,
                            form,
                            roleMeta,
                            defaultGenre,
                            defaultSubGenre
                        ),
                        cancelEditedChanges(setIsEditMode)
                    )}
            </div>
            <div
                className={classnames('general__main-section', {
                    editing: isEditMode,
                })}
            >
                <div className="general__main-info">
                    <div className="general-item">
                        <div className="general-item__label">Title</div>
                        <div className="general-item__value">
                            <Tooltip title={title || ''}>
                                <p className="title">{title}</p>
                            </Tooltip>
                        </div>
                    </div>
                    {seasonNumber && (
                        <div className="general-item">
                            <div className="general-item__label">Season</div>
                            <div className="general-item__value">{seasonNumber}</div>
                        </div>
                    )}
                    <div className="general-item">
                        <div className="general-item__label">Content Type</div>
                        <div className="general-item__value">{contentType}</div>
                    </div>
                    {getIsRequiredLabel('allotment', 'Allotment').includes('*') && (
                        <div className="general-item">
                            <div className="general-item__label">
                                {getIsRequiredLabel('allotment', 'Allotment')}
                            </div>
                            <div className="general-item__value">
                                {getAllotment(
                                    allotmentField.input.onChange,
                                    allotmentField.input.value as any,
                                    isEditMode,
                                    allotment,
                                    videoData?.video || defaultVideoData,
                                    getEmptyFieldError
                                )}
                            </div>
                        </div>
                    )}

                    {episodeNumber && (
                        <div className="general-item">
                            <div className="general-item__label">Episode</div>
                            <div className="general-item__value">{episodeNumber}</div>
                        </div>
                    )}
                    <div className="general-item">
                        <div className="general-item__label">UUID</div>
                        <div className="general-item__value">{getUUID(mamId)}</div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">
                            {getIsRequiredLabel('source-region', 'Source Region')}
                        </div>
                        <div
                            className={classnames('general-item__value', {
                                metadataValueError: sourceRegionField.meta.error,
                            })}
                        >
                            {getSourceRegion(
                                isEditMode,
                                sourceRegionField.input.onChange,
                                sourceRegionField.input.value,
                                sourceRegion,
                                getEmptyFieldError,
                                metadataContentTypeRequiredFields,
                                'source-region'
                            )}
                            {isEditMode && sourceRegionField.meta.error && (
                                <span className="value-error">
                                    {sourceRegionField.meta.error}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">Partner</div>
                        <div className="general-item__value">{partner}</div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">
                            {getIsRequiredLabel('release-date', 'Release Date')}
                        </div>
                        <div
                            className={classnames('general-item__value', {
                                metadataValueError: releaseDateField.meta.error,
                            })}
                        >
                            {getReleaseYearDatePicker(
                                isEditMode,
                                releaseDateField.input.value,
                                releaseDateField.input.onChange,
                                defaultReleaseDate,
                                getEmptyFieldError,
                                'release-date'
                            )}
                            {isEditMode && releaseDateField.meta.error && (
                                <span className="value-error">
                                    {releaseDateField.meta.error}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">Partner#</div>
                        <div className="general-item__value">{partnerHash}</div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">
                            {getIsRequiredLabel('copyright-holder', 'Copyright Holder')}
                        </div>
                        <div className="general-item__value">
                            {getTextValue(
                                isEditMode,
                                'copyright-holder',
                                copyrightHolderField.input.value,
                                copyrightHolderField.input.onChange,
                                getEmptyFieldError,
                                copyrightHolder
                            )}
                        </div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">PTV Avails ID</div>
                        <div className="general-item__value">{availsId}</div>
                    </div>
                    <div className="general-item">
                        <div className="general-item__label">Copyright Year</div>
                        <div className="general-item__value">
                            {getYearSelect(
                                isEditMode,
                                copyrightYearField.input.value,
                                copyrightYearField.input.onChange,
                                selectYearOptions,
                                copyrightYear
                            )}
                        </div>
                    </div>
                </div>
                <div className="general__genres">
                    <div className="general__genres-label">Genres*</div>
                    <div
                        className={classnames('general__genres-values', {
                            editMode: isEditMode,
                            metadataValueError: isEditMode && genreField.meta.error,
                        })}
                    >
                        {getGenresSelected(
                            isEditMode,
                            possibleGenreLabels,
                            genreField.input.value,
                            onGenreChange(
                                genreField.input.onChange,
                                subGenreField.input.onChange
                            ),
                            defaultGenreItem,
                            defaultGenreLabels,
                            getEmptyFieldError,
                            'genres'
                        )}
                        {isEditMode && genreField.meta.error && (
                            <span className="value-error">{genreField.meta.error}</span>
                        )}
                    </div>
                </div>
                <div className="general__subgenres">
                    <div className="general__subgenres-label">Sub-Genre*</div>
                    <div
                        className={classnames('general__subgenres-values', {
                            editMode: isEditMode,
                            metadataValueError: isEditMode && subGenreField.meta.error,
                        })}
                    >
                        {getSubGenresSelected(
                            isEditMode,
                            genreField.input.value,
                            subGenreField.input.value,
                            subGenreField.input.onChange,
                            defaultSubGenreItem,
                            defaultSubGenreLabels,
                            possibleSubGenreLabels,
                            getEmptyFieldError,
                            'sub-genres'
                        )}
                        {isEditMode && subGenreField.meta.error && (
                            <span className="value-error">
                                {subGenreField.meta.error}
                            </span>
                        )}
                    </div>
                </div>
                <div className="general__roles">
                    <div className="general__roles-label">Roles</div>
                    <div className="general__roles-values">
                        {getRoleItems(isEditMode, actorItemsEditMode, actorItems)}
                        {getRoleItems(isEditMode, directorItemsEditMode, directorItems)}
                        {getRoleItems(isEditMode, producerItemsEditMode, producerItems)}
                        {getRoleItems(isEditMode, writersItemsEditMode, writerItems)}
                        {getRoleItems(isEditMode, composersItemsEditMode, composerItems)}
                    </div>
                </div>
            </div>
        </>
    );
}

export default React.memo(ContentItemViewMetadataGeneral);
